import React from "react";
import { motion } from "framer-motion";
import { MapPin, Star, ShoppingCart, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/AppContext";
import api from "@/api";
import { Button } from "@/components/ui/button";

function SourceBadge({ source, t }) {
  if (source === "internal")
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-600 border border-emerald-500/40 rounded-full px-2 py-0.5">
        <CheckCircle2 size={11} /> {t("in_our_stock")}
      </span>
    );
  if (source === "marketplace")
    return (
      <span className="text-[10px] font-bold uppercase tracking-wider bg-blue-500/15 text-blue-600 border border-blue-500/40 rounded-full px-2 py-0.5">
        {t("marketplace")}
      </span>
    );
  return (
    <span className="text-[10px] font-bold uppercase tracking-wider bg-cyan-500/15 text-cyan-600 border border-cyan-500/40 rounded-full px-2 py-0.5">
      {t("global_supplier")}
    </span>
  );
}

export default function ResultsTable({ data }) {
  const { t, refreshCart } = useApp();

  const addToCart = async (r) => {
    try {
      await api.post("/cart/add", {
        pn: r.pn, name: r.name, price: r.price || 0,
        source: r.source, seller: r.seller, quantity: 1,
      });
      await refreshCart();
      toast.success(t("add_to_cart") + " ✓");
    } catch {
      toast.error("Error");
    }
  };

  if (!data) return null;
  const results = data.results || [];
  if (results.length === 0)
    return <p className="text-center text-slate-500 py-10" data-testid="no-results">{t("no_results")}</p>;

  return (
    <div data-testid="results-container">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h3 className="font-head text-xl font-bold text-slate-900">
          {t("results")} <span className="text-cyan-600">({results.length})</span>
        </h3>
        <div className="flex gap-2 text-xs font-mono-ps text-slate-500">
          <span className="text-emerald-600">MilSis: {data.counts?.internal || 0}</span>
          <span className="text-blue-600">Market: {data.counts?.marketplace || 0}</span>
          <span className="text-cyan-600">Global: {data.counts?.global || 0}</span>
        </div>
      </div>

      {data.ai_summary && (
        <div data-testid="ai-summary" className="mb-4 flex items-start gap-2.5 ps-glow-card rounded-xl p-3.5 border-cyan-500/40">
          <Sparkles size={18} className="text-cyan-600 mt-0.5 shrink-0" />
          <p className="text-sm text-slate-700">{data.ai_summary}</p>
        </div>
      )}

      <div className="space-y-3">
        {results.map((r, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(i * 0.04, 0.5) }}
            className={`ps-glow-card rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-3 ${
              r.source === "internal" ? "border-emerald-500/40" : ""
            }`}
            data-testid={`result-row-${i}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <SourceBadge source={r.source} t={t} />
                <span className="font-mono-ps text-cyan-700 text-sm font-bold tracking-wider">{r.pn}</span>
              </div>
              <p className="text-slate-900 font-medium truncate">{r.name}</p>
              <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                <span className="flex items-center gap-1"><MapPin size={12} /> {r.location || r.country || "—"}</span>
                <span className="flex items-center gap-1"><Star size={12} className="text-amber-500" /> {r.rating}</span>
                <span>{t("lead_time")}: {r.lead_time || "—"}</span>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className={`text-xs ${r.stock_status === "In Stock" ? "text-emerald-600" : "text-amber-500"}`}>
                  {r.stock_status}{r.quantity != null ? ` · ${r.quantity}` : ""}
                </p>
                <p className="font-display font-bold text-lg text-slate-900">
                  ${Number(r.price || 0).toLocaleString()}
                </p>
                <p className="text-[11px] text-slate-500">{r.condition}{r.seller && r.seller !== "GetFPV" ? ` · ${r.seller}` : ""}</p>
              </div>
              <div className="flex flex-col gap-1.5">
                <Button
                  data-testid={`add-cart-btn-${i}`}
                  size="sm"
                  onClick={() => addToCart(r)}
                  className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold"
                >
                  <ShoppingCart size={14} className="mr-1" /> {t("add_to_cart")}
                </Button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
